import { Box, Grid } from "@material-ui/core";
import { useEffect, useRef, useState, useMemo } from "react";
import { fget } from "../../../API/callsAPI";
import useBatchResetManager from "../../../Hooks/SequencerHooks/useBatchResetManager";
import useUrlBatchLoader from "../../../Hooks/SequencerHooks/useUrlBatchLoader";
import ErrorOrSuccessMessage from "../../Utilities/ErrorOrSuccessMessage/ErrorOrSuccessMessage.component";
import Loader from "../../Utilities/Loader/Loader";
import SequencerAccessDenied from "../SequencerTools/SequencerAccessDenied";
import SequencerMixTaskAndSequenceViewer from "../PlayerView/SequencerMixTaskAndSequenceViewer";
import SequencerMixTimeline from "../PlayerView/SequencerMixTimeline";
import SequencerMixVideoPlayer from "../PlayerView/SequencerMixVideoPlayer";
import SequencerMixDataView from "../DataView/SequencerMixDataView";
import SequencerHeader from "../SequencerTools/SequencerHeader";
import { Modal } from "@mui/material";
import AddShotToSequencerTimelineModal from "../SequencerModal/AddShotToSequencerTimelineModal";
import sequencerMixNewStyles from "./SequencerMixNewStyles";

const SequencerOverview = ({ params }) => {
  const classes = sequencerMixNewStyles();
  const [viewMode, setViewMode] = useState("player");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [isVideosLoaded, setIsVideosLoaded] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [sequencerDetails, setSequencerDetails] = useState({});
  const [forbidden, setForbidden] = useState(false);
  const [snackValue, setSnackValue] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });

  const [videoDurations, setVideoDurations] = useState([]);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // <-- Add currentTime state
  const [seekToTime, setSeekToTime] = useState(0);
  const [shotsLineUp, setShotsLineUp] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [canvasModal, setCanvasModal] = useState(false);
  const [resetAll, setResetAll] = useState(0);
  const [isAddShotToTimelineModalOpen, setIsAddShotToTimelineModalOpen] =
    useState(false);
  const [currentShotsInTimeline, setCurrentShotsInTimeline] = useState([]);
  const [addBetweenShots, setAddBetweenShots] = useState([]);
  const playerContainerRef = useRef(null);
  const { registerUrlReset, registerVideoReset, resetAllBatches } =
    useBatchResetManager(setRefreshCounter, resetAll);

  const totalDuration = useMemo(() => videoDurations.reduce((a, b) => a + b, 0), [videoDurations]);
  const scaleWidth = 1000;
  const pixelsPerSecond = totalDuration ? scaleWidth / totalDuration : 0;

  const handleViewModeChange = (mode) => {
    if (mode !== viewMode) {
      setSeekToTime(currentTime);
    }
    setViewMode(mode);
  };

  useEffect(() => {
    const handleGetTimeline = async () => {
      try {
        const res = await fget({
          url: `sequencers/timelines/${params?.playlistId}/`,
        });
        setSequencerDetails(res?.data);
      } catch (error) {
        if (error?.response?.status === 403) {
          setForbidden(true);
        }
        console.log(error);
      }
    };
    handleGetTimeline();
  }, [params?.playlistId]);

  useEffect(() => {
    const handleGetShotReprs = async () => {
      setIsVideosLoaded(false);
      try {
        const layersRes = await fget({
          url: `sequencers/layers/?timeline=${params?.playlistId}`,
        });
        const res = await fget({
          url: `sequencers/shot-reprs/?layer=${layersRes?.data?.results[0].id}`,
        });
        setCurrentShotsInTimeline(res?.data?.results);

        // Add batch numbers based on batch size of 15
        const URL_BATCH_SIZE = 15;
        const VIDEO_BATCH_SIZE = 5;
        const allShotReprs = res?.data?.results;
        const localLineup = [];
        let prevFps = 0;
        const fps = sequencerDetails?.frame_rate || 25;
        for (let i = 0; i < allShotReprs.length; i++) {
          prevFps = allShotReprs[i - 1]?.end_frame || 0;
          localLineup[i] = {
            id: allShotReprs[i].id,
            index: i,
            start: 0,
            end: Math.round(
              (allShotReprs[i].end_frame - allShotReprs[i].start_frame) / fps,
            ),
            fpsStart: allShotReprs[i].start_frame,
            fpsEnd: allShotReprs[i].end_frame,
            label: allShotReprs[i].shot_version,
            shot: allShotReprs[i].shot,
            shotVersion: allShotReprs[i].shot_version,
            urlBatchNumber: Math.floor(i / URL_BATCH_SIZE),
            videoBatchNumber: Math.floor(i / VIDEO_BATCH_SIZE),
            previousEnd: prevFps / fps,
            phase: allShotReprs[i].phase,
          };
        }
        setShotsLineUp(localLineup);
        //set video durations for thumbnails
        const videoDurations = localLineup.map((shot) => shot.end);
        setVideoDurations(videoDurations);
        setIsVideosLoaded(true);

      } catch (error) {
        setIsVideosLoaded(true);
        if (error?.response?.status === 403) {
          setForbidden(true);
        }
        console.log(error);
      }
    };
    if (params?.playlistId && sequencerDetails?.id) {
      handleGetShotReprs();
    }
  }, [params?.playlistId, refreshCounter, sequencerDetails]);

  const {
    currentUrlBatch,
    setCurrentUrlBatch,
    loadedUrls,
    startedLoadingBatches,
    forceReloadBatch,
    resetUrlBatchStates,
  } = useUrlBatchLoader(
    shotsLineUp,
    params,
    setThumbnails,
    setShotsLineUp,
    setResetAll,
  );

  useEffect(() => {
    registerUrlReset(resetUrlBatchStates);
  }, [resetUrlBatchStates, registerUrlReset]);

  if (forbidden) {
    return <SequencerAccessDenied projectId={params?.projectId} />;
  }

  return (
    <div
      ref={playerContainerRef}
      tabIndex={0}
      className={classes.overviewRootStyle}
      style={{ height: "calc(100vh - 118px)" }}
    >
      <ErrorOrSuccessMessage snackValue={snackValue} />
      <SequencerHeader
        projectId={params?.projectId}
        sequencerName={sequencerDetails?.name}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onExportClick={() => setIsExportModalOpen(true)}
        isExporting={isExporting}
        exportDisabled={shotsLineUp.length === 0}
        selectedVersion={selectedVersion}
      />
      {viewMode === "player" ? (
        <Box className={classes.viewBodyStyle}>
          <Box style={{ flex: 3, minHeight: 0, overflow: "hidden" }}>
            <Grid
              container
              spacing={1}
              style={{
                height: "100%",
                overflow: "hidden",
              }}
            >
              <Grid item md={9} style={{ height: "100%" }}>
                {isVideosLoaded ? (
                  <SequencerMixVideoPlayer
                    currentTime={currentTime}
                    setCurrentTime={setCurrentTime}
                    isVideoPlaying={isVideoPlaying}
                    sequencerDetails={sequencerDetails}
                    projectId={params?.projectId}
                    currentUrlBatch={currentUrlBatch}
                    setCurrentUrlBatch={setCurrentUrlBatch}
                    loadedUrls={loadedUrls}
                    orderedVideo={shotsLineUp}
                    seekToTime={seekToTime}
                    setIsVideoPlaying={setIsVideoPlaying}
                    startedLoadingBatches={startedLoadingBatches}
                    playerContainerRef={playerContainerRef}
                    forceReloadBatch={forceReloadBatch}
                    setSelectedVersion={setSelectedVersion}
                    setScreenshot={setScreenshot}
                    canvasModal={canvasModal}
                    registerVideoReset={registerVideoReset}
                    setResetAll={setResetAll}
                    isExportModalOpen={isExportModalOpen}
                    setIsExportModalOpen={setIsExportModalOpen}
                    isExporting={isExporting}
                    setIsExporting={setIsExporting}
                    totalDuration={totalDuration}
                  />
                ) : (
                  <Loader />
                )}
              </Grid>
              <Grid item md={3} style={{ height: "100%" }}>
                <SequencerMixTaskAndSequenceViewer
                  sequences={sequencerDetails?.sequences}
                  projectId={params?.projectId}
                  templateId={sequencerDetails?.template}
                  playlistId={params?.playlistId}
                  setSnackValue={setSnackValue}
                  selectedVersion={selectedVersion}
                  currentTime={currentTime}
                  setScreenshot={setScreenshot}
                  screenshot={screenshot}
                  canvasModal={canvasModal}
                  setCanvasModal={setCanvasModal}
                  isVideoPlaying={isVideoPlaying}
                  resetAllBatches={resetAllBatches}
                  phase={shotsLineUp[0]?.phase}
                  handleShotsLineUp={setShotsLineUp}
                  shotsLineUp={shotsLineUp}
                  setIsAddShotToTimelineModalOpen={setIsAddShotToTimelineModalOpen}
                  addBetweenShots={addBetweenShots}
                  setAddBetweenShots={setAddBetweenShots}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            style={{
              width: "100%",
              backgroundColor: "#252A38",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
              minHeight: 0,
              overflowY: "hidden",
              boxSizing: "border-box",
              borderRadius: "10px",
            }}
          >
            <SequencerMixTimeline
              videoDurations={videoDurations}
              currentTime={currentTime}
              setCurrentTime={setCurrentTime}
              thumbnails={thumbnails}
              setSeekToTime={setSeekToTime}
              setIsVideoPlaying={setIsVideoPlaying}
              shotsLineUp={shotsLineUp}
            />
          </Box>
        </Box>
      ) : (
        <Box className={classes.viewBodyStyle}>
          <SequencerMixDataView
            isVideosLoaded={isVideosLoaded}
            currentTime={currentTime}
            setCurrentTime={setCurrentTime}
            totalDuration={totalDuration}
            isVideoPlaying={isVideoPlaying}
            sequencerDetails={sequencerDetails}
            projectId={params?.projectId}
            currentUrlBatch={currentUrlBatch}
            setCurrentUrlBatch={setCurrentUrlBatch}
            loadedUrls={loadedUrls}
            shotsLineUp={shotsLineUp}
            seekToTime={seekToTime}
            setIsVideoPlaying={setIsVideoPlaying}
            startedLoadingBatches={startedLoadingBatches}
            playerContainerRef={playerContainerRef}
            forceReloadBatch={forceReloadBatch}
            setSelectedVersion={setSelectedVersion}
            setScreenshot={setScreenshot}
            canvasModal={canvasModal}
            registerVideoReset={registerVideoReset}
            setResetAll={setResetAll}
            isExportModalOpen={isExportModalOpen}
            setIsExportModalOpen={setIsExportModalOpen}
            isExporting={isExporting}
            setIsExporting={setIsExporting}
            setSeekToTime={setSeekToTime}
            sequenceId={sequencerDetails?.sequences?.[0]?.id}
            selectedVersion={selectedVersion}
            refreshCounter={refreshCounter}
            setRefreshCounter={setRefreshCounter}
            screenshot={screenshot}
            setCanvasModal={setCanvasModal}
          />
        </Box>
      )}
      <Modal
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={isAddShotToTimelineModalOpen}
        onClose={() => setIsAddShotToTimelineModalOpen(false)}
      >
        <AddShotToSequencerTimelineModal
          sequencesInTimeline={sequencerDetails?.sequences}
          projectId={params?.projectId}
          playListId={params?.playlistId}
          currentShotsInTimeline={currentShotsInTimeline}
          closeModal={() => setIsAddShotToTimelineModalOpen(false)}
          resetAllBatches={resetAllBatches}
          addBetweenShots={addBetweenShots}
          setAddBetweenShots={setAddBetweenShots}
        />
      </Modal>
    </div>
  );
};

export default SequencerOverview;
